import dayjs from 'dayjs';
import { toEnumFromString, fromEnumAsString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CalendarEntry } from '../calendar-entry.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, DataModelBuilder, isSameData, SerializedData } from '../dto/dto.js';
import { NotificationEvent, SyncEventSource } from '../sync-event.js';

export class CancelNotConfirmedEvent extends NotificationEvent {
  static readonly TYPE_ID: string = '20a62e29eb384e1482ba2ef3d6aff9cf';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly seen: boolean,
    public readonly entry: CalendarEntry,
  ) {
    super();
  }

  get $typeId(): string {
    return CancelNotConfirmedEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, CancelNotConfirmedEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CancelNotConfirmedEvent {
    return new CancelNotConfirmedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
        context.fromData<CalendarEntry>(MapUtils.get<SerializedData>(map, 'entry')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'seen': this.seen,
      'entry': context.toData(this.entry),
    };
  }

  clone(): this {
    return new CancelNotConfirmedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
        this.entry.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.seen === this.seen &&
        isSameData(other.entry, this.entry);
  }

  markAsSeen(): this {
    return new CancelNotConfirmedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.entry.clone(),
    ) as this;
  }
}

export class CancelNotConfirmedEventBuilder extends DataModelBuilder<CancelNotConfirmedEvent> {
  build(context: SerializationContext, map: SerializedData): CancelNotConfirmedEvent {
    return CancelNotConfirmedEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CancelNotConfirmedEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CancelNotConfirmedEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
